import KeyboardIcon from "@mui/icons-material/Keyboard";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import React, { useMemo } from "react";

import { DISPATCH_ACTION_LABELS, type DispatchAction } from "@storyboard/dispatch-view/view";
import { TestIds } from "@storyboard/web-test-ids/testIds";
import { dispatchShortcutEntries } from "@/hooks/dispatch/dispatchKeymap";
import { zinc } from "@/ui/tokens";

function Keycap({ children }: { children: React.ReactNode }) {
  return (
    <Box
      component="kbd"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 20,
        height: 20,
        px: 0.625,
        borderRadius: 0.75,
        bgcolor: zinc[800],
        border: `1px solid ${zinc[600]}`,
        color: zinc[200],
        fontSize: "0.6875rem",
        fontWeight: 700,
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        lineHeight: 1,
      }}
    >
      {children}
    </Box>
  );
}

/** A shortcut's label beside its keycaps in the bar. */
const labelSx = { fontSize: "0.6875rem", color: zinc[400], ml: 0.25, whiteSpace: "nowrap" } as const;

/**
 * The keyboard button in the header: it folds the shortcut bar open and shut, and the full list drops from it.
 *
 * The fold is a page setting, the member's over the workspace's. The `showShortcuts` key opens the full list rather
 * than folding the bar, so the button is the only way to fold it.
 */
export const DispatchShortcutsButton = React.memo(function DispatchShortcutsButton({
  shown,
  onToggle,
  buttonRef,
}: {
  shown: boolean;
  onToggle: () => void;
  /** The button element, which the full list anchors under. */
  buttonRef?: React.Ref<HTMLButtonElement>;
}) {
  return (
    <Tooltip title={shown ? "Hide shortcut bar" : "Show shortcut bar"}>
      <IconButton
        ref={buttonRef}
        data-sb={TestIds.dispatch.shortcuts}
        aria-label="Shortcut bar"
        aria-pressed={shown}
        size="small"
        onClick={onToggle}
        sx={{
          color: shown ? "white" : zinc[300],
          bgcolor: shown ? zinc[600] : zinc[700],
          borderRadius: 1,
          height: 32,
          width: 32,
          "&:hover": { bgcolor: zinc[600] },
        }}
      >
        <KeyboardIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
});

/**
 * The keys that fire one shortcut, "or"-separated, so the arrows and J/K read as alternatives to Tab rather than a chord
 * with it.
 */
export function ShortcutKeys({ keys }: { keys: readonly string[][] }) {
  return (
    <>
      {keys.map((group, index) => (
        <React.Fragment key={group.join("+")}>
          {index > 0 && <Typography sx={{ fontSize: "0.625rem", color: zinc[500] }}>or</Typography>}
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}>
            {group.map((cap) => (
              <Keycap key={cap}>{cap}</Keycap>
            ))}
          </Box>
        </React.Fragment>
      ))}
    </>
  );
}

/**
 * The shortcut bar: one row under the header naming every shortcut and its keycaps, always in view while unfolded so a
 * new dispatcher never has to remember them, and folded away by the keyboard button once they do. The order is the
 * helper's (`dispatchShortcutEntries`), the same for every workspace; a workspace's own bindings change the keys, never
 * the places. One row, never two: on a narrow window the row scrolls sideways rather than pushing the table down,
 * while All shortcuts stays pinned at the right edge, a button that opens the full list.
 */
export const DispatchShortcutsBar = React.memo(function DispatchShortcutsBar({
  keymap,
  onShowAll,
}: {
  keymap: Record<DispatchAction, string>;
  /** Opens the full list, from the pinned All shortcuts entry. */
  onShowAll: () => void;
}) {
  const entries = useMemo(() => dispatchShortcutEntries(keymap), [keymap]);
  const listed = entries.filter((entry) => entry.action !== "showShortcuts");
  const showAll = entries.find((entry) => entry.action === "showShortcuts");
  return (
    <Box
      data-sb={TestIds.dispatch.keyboardLegend}
      sx={{
        display: "flex",
        alignItems: "center",
        bgcolor: "rgba(0, 0, 0, 0.25)",
        borderTop: `1px solid ${zinc[800]}`,
        borderBottom: `1px solid ${zinc[800]}`,
      }}
    >
      <Box
        sx={{
          flex: "1 1 auto",
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "nowrap",
          overflowX: "auto",
          px: 2,
          py: 0.5,
        }}
      >
        {listed.map((entry) => (
          <Box
            key={entry.action}
            data-sb={TestIds.dispatch.shortcutEntry}
            sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
          >
            <ShortcutKeys keys={entry.keys} />
            <Typography data-sb={TestIds.dispatch.shortcutLabel} sx={labelSx}>
              {entry.label}
            </Typography>
          </Box>
        ))}
      </Box>
      <ButtonBase
        data-sb={TestIds.dispatch.allShortcuts}
        onClick={onShowAll}
        sx={{
          flex: "none",
          gap: 0.5,
          px: 2,
          py: 0.5,
          alignSelf: "stretch",
          borderLeft: `1px solid ${zinc[800]}`,
          "&:hover": { bgcolor: zinc[800] },
        }}
      >
        {showAll && <ShortcutKeys keys={showAll.keys} />}
        <Typography data-sb={TestIds.dispatch.shortcutLabel} sx={{ ...labelSx, color: zinc[200] }}>
          {DISPATCH_ACTION_LABELS.showShortcuts}
        </Typography>
      </ButtonBase>
    </Box>
  );
});
